import { create } from "zustand";

// AuthTokens groups the credentials the AS hands the SPA on a successful
// /token exchange. The refresh token is deliberately NOT part of this shape:
// it travels only as the __Host-as-refresh HttpOnly cookie the AS sets on
// /token, so it is never JS-readable and never persisted here (R1 finding —
// a localStorage-held refresh token is a durable account-takeover surface
// for any XSS on the page). accessToken/idToken live in memory ONLY (this
// zustand store, no localStorage/sessionStorage) so a page reload starts
// with an empty store; ProtectedRoute's beginLogin() re-establishes the
// session via the still-live AS session cookie.
// appToken/appTokenExpiresAt (SCENARIO-08) are the SECOND token the SPA
// holds: edge-app mints it in exchange for accessToken (the AS token,
// above) once per callback/refresh cycle, scoped to the caller's account.
// They live in the SAME in-memory-only shape and are cleared by
// clearAuthTokens() exactly like the AS token — deliberately: an AS token
// that dies (401/403 -> clearAuthTokens) cannot re-exchange, so keeping a
// live app token past it would be a scoping token outliving its identity.
export interface AuthTokens {
  readonly accessToken: string | null;
  readonly idToken: string | null;
  readonly accessTokenExpiresAt: number | null;
  readonly sub: string | null;
  readonly appToken: string | null;
  readonly appTokenExpiresAt: number | null;
}

const EMPTY_TOKENS: AuthTokens = {
  accessToken: null,
  idToken: null,
  accessTokenExpiresAt: null,
  sub: null,
  appToken: null,
  appTokenExpiresAt: null,
};

export interface AuthState extends AuthTokens {
  setTokens(tokens: AuthTokens): void;
  clearTokens(): void;
}

export const useAuthStore = create<AuthState>((set) => ({
  ...EMPTY_TOKENS,
  setTokens: (tokens: AuthTokens) => set((state) => ({ ...state, ...tokens })),
  clearTokens: () => set((state) => ({ ...state, ...EMPTY_TOKENS })),
}));

export function useAuth() {
  const { accessToken, idToken, accessTokenExpiresAt, sub, appToken, appTokenExpiresAt, setTokens, clearTokens } =
    useAuthStore((s) => s);

  return {
    token: accessToken,
    idToken,
    accessTokenExpiresAt,
    sub,
    appToken,
    appTokenExpiresAt,
    setTokens,
    clearTokens,
    // isAuthenticated stays keyed on the AS token, not appToken (S09): the AS
    // token is still what says "signed in". The app token is a scoping
    // credential derived from it, and a transient exchange failure must not
    // render as "signed out" and race ProtectedRoute into beginLogin.
    isAuthenticated: Boolean(accessToken),
  };
}

// Non-hook accessors for use inside non-React contexts (fetch middleware,
// imperatively-triggered refresh flow). Reads/writes route through the same
// in-memory store so React subscribers stay in sync after a setState.
export function readAuthTokens(): AuthTokens {
  return useAuthStore.getState();
}

export function writeAuthTokens(tokens: AuthTokens): void {
  useAuthStore.getState().setTokens(tokens);
}

export function clearAuthTokens(): void {
  useAuthStore.getState().clearTokens();
}
